import React, { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { ModalContextConsumer } from '../../../contexts/modalContext';
import UserTileContext from '../../../contexts/userTilesContext';
import ModalHelpers from '../../../helpers/ModalHelpers/ModalHelpers';

// interface
import { IContactModuleProps, IModalProps, ISaveableContact } from '../../../interfaces/interfaces';
import { saveConfiguration } from '../../../utils/data';
import getNextFreeSlot from '../../../utils/get-next-free-slot';
import keyboardTriggerBasic from '../../../utils/keyboard-trigger';

// module, source
import Header from '../../elements/Header/Header';
import Link from '../../elements/Link/Link';
import TileWrapper from '../../elements/TileWrapper/TileWrapper';

function ContactModule(props: IContactModuleProps): JSX.Element {
  const {
    headline,
    tileId,
    isEditable,
    hideMenu,
    contactName,
    contactDescription,
    contactPhone,
    contactPhoneMobile,
    contactEmail,
    tileRefId
  } = props;

  // EDIATBLE STATES
  const [updatedName, updateName] = useState(contactName);
  const [updatedDescription, updateDescription] = useState(contactDescription);
  const [updatedPhone, updatePhone] = useState(contactPhone);
  const [updatedPhoneMobile, updatePhoneMobile] = useState(contactPhoneMobile);
  const [updatedEmail, updateEmail] = useState(contactEmail);
  const [showEditHint, setShowEditHint] = useState(false);

  // UPDATING DATA
  const userTiles = useContext(UserTileContext);
  const updateTileAndSave = (tileProps: ISaveableContact) => {
    const allTiles = userTiles.tiles;
    const updatedSet = allTiles.map((t) => {
      if (t.id === tileId) {
        return { ...t, props: { ...t.props, ...tileProps } };
      }
      return t;
    });
    userTiles.setTiles(updatedSet);
    saveConfiguration(updatedSet, userTiles.settings).catch(() => {
      // TODO: better error messages
      alert('Speichern fehlgeschlagen');
    });
  };

  // EDIT MODE
  const isEditing = false;
  const toggleEditing = (openModal: (modal: IModalProps) => void) => {
    ModalHelpers.openContactFormModal(
      openModal,
      // contact data
      {
        ...props,
        contactEmail: updatedEmail || '',
        contactName: updatedName || '',
        contactDescription: updatedDescription || '',
        contactPhone: updatedPhone || '',
        contactPhoneMobile: updatedPhoneMobile || '',
      },
      // onConfirm
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (confirmData: any) => {
        // update internal
        updateName(confirmData.updatedName);
        updateDescription(confirmData.updatedDescription);
        updatePhone(confirmData.updatedPhone);
        updatePhoneMobile(confirmData.updatedPhoneMobile);
        updateEmail(confirmData.updatedEmail);

        // save
        updateTileAndSave({
          contactEmail: confirmData.updatedEmail || '',
          contactName: confirmData.updatedName || '',
          contactDescription: confirmData.updatedDescription || '',
          contactPhone: confirmData.updatedPhone || '',
          contactPhoneMobile: confirmData.updatedPhoneMobile || '',
        });
      },
      // onAbort
      () => {
        /**/
      },
    );
  };

  useEffect(() => {
    const dataStr = `${(updatedName || '').trim()}${(updatedDescription || '').trim()}${(updatedPhone || '').trim()}${(
      updatedPhoneMobile || ''
    ).trim()}${(updatedEmail || '').trim()}`;

    if (dataStr.length === 0 && !isEditing) {
      setShowEditHint(true);
      return;
    }

    setShowEditHint(false);
  }, [isEditing, updatedName, updatedDescription, updatedPhone, updatedPhoneMobile, updatedEmail]);

  const contactOutput = (
    <>
      <div className="contact-info contact-name">
        <h5 className="hl-5">{updatedName || ''}</h5>
      </div>
      <div className={`contact-info contact-description ${!updatedDescription?.trim().length ? 'is-empty' : ''}`}>
        <p className="tx_2">{updatedDescription || ''}</p>
      </div>
      <div className={`contact-info contact-phone ${!updatedPhone?.trim().length ? 'is-empty' : ''}`}>
        <Link linkHref={`tel:${updatedPhone}`} linkText={updatedPhone} linkClass="link multiple-link ic-phone" />
      </div>
      <div className={`contact-info contact-phone-mobile ${!updatedPhoneMobile?.trim().length ? 'is-empty' : ''}`}>
        <Link
          linkHref={`tel:${updatedPhoneMobile}`}
          linkText={updatedPhoneMobile}
          linkClass="link  multiple-link ic-mobile"
        />
      </div>
      <div className={`contact-info contact-email ${!updatedEmail?.trim().length ? 'is-empty' : ''}`}>
        <Link linkHref={`mailto:${updatedEmail}`} linkText={updatedEmail} linkClass="link  multiple-link ic-mail" />
      </div>
    </>
  );

  const createNewContactTile = () => {
    const newTileId = `new${Math.random() * 1000}`;
    const nextFreeSlot = getNextFreeSlot(userTiles.tiles, 1);
    userTiles.addTile({
      id: newTileId,
      module: 'ContactModule',
      tileRefId,
      props: {
        headline,
        contactName: '',
        contactDescription: '',
        contactPhone: '',
        contactPhoneMobile: '',
        contactEmail: '',
      } as IContactModuleProps,
      layout: { i: 'ss', x: nextFreeSlot.x, y: nextFreeSlot.y, w: 1, h: 1 },
    });
  };

  return (
    <>
      <ModalContextConsumer>
        {({ openModal }) => (
          <div className="tile-body">
            {headline ? (
              <Header
                headline={headline}
                tileId={tileId}
                onToggleEditing={() => {
                  toggleEditing(openModal);
                }}
                isEditing={isEditing}
                hideMenu={hideMenu}
                isEditable={isEditable}
                canCreateNew
                onCreateNew={createNewContactTile}
              />
            ) : (
              ''
            )}
            <TileWrapper className={`tile-padding ${isEditing ? 'is-editing' : ''}`} clickable={false}>
              <>
                {showEditHint ? (
                  <div className="edit-hint">
                    <p>
                      <Trans i18nKey="cockpit.contactmodule.emptyHint">Fügen Sie Ihren Kontakt ein: Klicken Sie auf <em>Bearbeiten</em> im Kachelmenü</Trans>
                    </p>
                    <a
                      role="button"
                      className="link tile-link ic-arrowRight"
                      onClick={() => {
                        toggleEditing(openModal);
                      }}
                      onKeyDown={(e) => {
                        keyboardTriggerBasic(e, () => {
                          toggleEditing(openModal);
                        });
                      }}
                      tabIndex={0}
                    >
                      <Trans i18nKey="cockpit.contactmodule.emptyLinkText">Kontakt eintragen</Trans>
                    </a>
                  </div>
                ) : (
                  contactOutput
                )}
              </>
            </TileWrapper>
          </div>
        )}
      </ModalContextConsumer>
    </>
  );
}
export default ContactModule;
